import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';

import Validator, { useValidate, checkValidity } from '../../../../components/validator';

import Auth from '../../../../data/auth-service';
import DataContext from '../../../../data/data-context';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
    input: {
        width: '100%',
    },
    shortInput: {
        width: '120px',
    },
}));

const AuthStandard = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { systemSettings, updateSystemSettings } = useContext(DataContext);
    const [standard, setStandard] = useState({
        concurrent_logins: 0,
        password_expire_days: 90,
        password_lockout_count: 11,
        password_lockout_minutes: 60,
        password_unique_count: 10,
        session_timeout: 1440,
        password_change_rate: {
            changes: 1,
            hour: 0,
        },
    });
    const [changes, setChanges] = useState(false);
    const standardForm = useValidate({});
    const standardPerms = Auth.permissions.get('AuthenticationStandard');

    useEffect(() => {
        if (systemSettings && systemSettings.auth) {
            const s = _cloneDeep(systemSettings.auth.standard);
            setStandard(s);
        }
    }, [systemSettings]);

    useEffect(() => {
        if (systemSettings && systemSettings.auth) {
            setChanges(_isEqual(standard, systemSettings.auth.standard));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [standard]);

    const handleUpdateStandard = (e, validID, valid) => {
        standardForm.set({ ...standardForm.get(), [validID]: valid });

        setStandard({
            ...standard,
            [e.target.name]: e.target.type === 'number' ? parseInt(e.target.value) : e.target.value,
        });
    };

    const handlePasswordChanges = (e, validID, valid) => {
        standardForm.set({ ...standardForm.get(), [validID]: valid });

        const s = { ...standard };
        s.password_change_rate.changes = parseInt(e.target.value);
        setStandard(s);
    };

    const handlePasswordHour = (e, validID, valid) => {
        standardForm.set({ ...standardForm.get(), [validID]: valid });

        const s = { ...standard };
        s.password_change_rate.hour = parseInt(e.target.value);
        setStandard(s);
    };

    const handleSave = () => {
        const s = { ...systemSettings };
        s.auth.standard = standard;
        updateSystemSettings({ auth: s.auth });
    };

    const handleCancel = () => {
        navigate('/system/configuration');
    };

    return (
        <Grid container spacing={5}>
            <Grid item xs={6}>
                <Box>
                    <Typography>Session Timeout</Typography>
                    <Validator
                        disabled={!standardPerms.write}
                        valueType="range"
                        errorText="Session timeout must be within 5 and 1440 minutes"
                        className={classes.input}
                        type="number"
                        inputProps={{
                            min: 5,
                            max: 1440,
                        }}
                        endAdornment={<InputAdornment position="end">Minutes</InputAdornment>}
                        name="session_timeout"
                        value={standard.session_timeout}
                        onChange={handleUpdateStandard}
                    />
                    <FormHelperText>User interface session timeout. Valid range is 5 to 1440 minutes.</FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>Concurrent Logins</Typography>
                    <Validator
                        disabled={!standardPerms.write}
                        valueType="range"
                        errorText="Concurrent logins must be within 0 and 11"
                        className={classes.input}
                        type="number"
                        inputProps={{
                            min: 0,
                            max: 11,
                        }}
                        endAdornment={<InputAdornment position="end">Logins</InputAdornment>}
                        name="concurrent_logins"
                        value={standard.concurrent_logins}
                        onChange={handleUpdateStandard}
                    />
                    <FormHelperText>Number of concurrent logins. Older sessions will be terminated when this number is exceeded. Set logins to zero to disable.</FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>Password Expiration</Typography>
                    <Validator
                        disabled={!standardPerms.write}
                        valueType="range"
                        errorText="Password expiration must be within 0 and 365"
                        className={classes.input}
                        type="number"
                        inputProps={{
                            min: 0,
                            max: 365,
                        }}
                        endAdornment={<InputAdornment position="end">Days</InputAdornment>}
                        name="password_expire_days"
                        value={standard.password_expire_days}
                        onChange={handleUpdateStandard}
                    />
                    <FormHelperText>Passwords will expire after this interval. Valid range is 1-365 days. Set days to zero to disable.</FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>Password Changes</Typography>
                    <Validator
                        disabled={!standardPerms.write}
                        valueType="range"
                        errorText="Password change must be within 1 and 10"
                        className={classes.shortInput}
                        type="number"
                        inputProps={{
                            min: 0,
                            max: 10,
                        }}
                        endAdornment={<InputAdornment position="end">Change</InputAdornment>}
                        value={standard.password_change_rate.changes}
                        onChange={handlePasswordChanges}
                    />
                    <Box display="inline" mx={5}>per</Box>
                    <Validator
                        disabled={!standardPerms.write}
                        valueType="range"
                        errorText="Change time must be within 0 and 72 hours"
                        className={classes.shortInput}
                        type="number"
                        inputProps={{
                            min: 0,
                            max: 72,
                        }}
                        endAdornment={<InputAdornment position="end">Hours</InputAdornment>}
                        value={standard.password_change_rate.hour}
                        onChange={handlePasswordHour}
                    />
                    <FormHelperText>Maximum rate for user password changes. Set hours to zero to disable.</FormHelperText>
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box>
                    <Typography>Password History</Typography>
                    <Validator
                        disabled={!standardPerms.write}
                        valueType="range"
                        errorText="Password history must be within 1 and 100"
                        className={classes.input}
                        type="number"
                        inputProps={{
                            min: 1,
                            max: 100,
                        }}
                        endAdornment={<InputAdornment position="end">Passwords</InputAdornment>}
                        name="password_unique_count"
                        value={standard.password_unique_count}
                        onChange={handleUpdateStandard}
                    />
                    <FormHelperText>Length of password history. Valid range is 1-100. As set, users will not be able to reuse any of their previous 10 passwords.</FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>Password Lockout</Typography>
                    <Validator
                        disabled={!standardPerms.write}
                        valueType="range"
                        errorText="Password lockout must be within 0 and 11"
                        className={classes.input}
                        type="number"
                        inputProps={{
                            min: 0,
                            max: 11,
                        }}
                        endAdornment={<InputAdornment position="end">Bad Passwords</InputAdornment>}
                        name="password_lockout_count"
                        value={standard.password_lockout_count}
                        onChange={handleUpdateStandard}
                    />
                    <FormHelperText>Number of wrong passwords allowed before the account is locked out. Valid range is 0-11. Set to zero to disable.</FormHelperText>
                </Box>
                <Box mt={3}>
                    <Typography>Lockout Period</Typography>
                    <Validator
                        disabled={!standardPerms.write}
                        valueType="range"
                        errorText="Password lockout period must be within 0 and 2880"
                        className={classes.input}
                        type="number"
                        inputProps={{
                            min: 0,
                            max: 2880,
                        }}
                        endAdornment={<InputAdornment position="end">Minutes</InputAdornment>}
                        name="password_lockout_minutes"
                        value={standard.password_lockout_minutes}
                        onChange={handleUpdateStandard}
                    />
                    <FormHelperText>An Account will be locked for this interval. Valid range is 0 to 2880 minutes. Set minutes to zero for no expiration period. <b>As set, accounts will require a password reset to unlock.</b></FormHelperText>
                </Box>
            </Grid>
            {standardPerms.write && <Grid item xs={12}>
                <Button
                    className={classes.buttonRight}
                    variant="contained"
                    color="primary"
                    aria-label="save"
                    onClick={handleSave}
                    disabled={!(checkValidity(standardForm.get()) && !changes)}
                >Save</Button>
                <Button className={classes.buttonRight} variant="contained" color="secondary" aria-label="cancel" onClick={handleCancel}>Cancel</Button>
            </Grid>}
        </Grid>
    );
};

export default AuthStandard;
